import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GraduationCap, Sparkles, Loader2, BrainCircuit, Stethoscope, HeartPulse, UserCheck } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  'Registered Nurse (RN)',
  'Licensed Practical Nurse (LPN)',
  'Certified Nursing Assistant (CNA)',
  'Travel Nurse',
  'ICU Nurse',
  'ER Nurse',
  'OR Nurse',
  'Labor & Delivery Nurse',
  'Pediatric Nurse',
  'Oncology Nurse',
  'Home Health Aide',
  'Physical Therapist',
  'Occupational Therapist',
  'Speech-Language Pathologist',
  'Respiratory Therapist',
  'Medical-Surgical Nurse',
  'Telemetry Nurse',
  'Psychiatric Nurse',
  'Nurse Practitioner',
  'Physician Assistant',
];

const SPECIALTIES = [
  'Critical Care / ICU',
  'Emergency Medicine',
  'Medical-Surgical',
  'Pediatrics',
  'Oncology',
  'Cardiology',
  'Orthopedics',
  'Neurology',
  'Labor & Delivery',
  'Geriatrics',
  'Psychiatry / Behavioral Health',
  'Home Health',
  'Long-Term Care / SNF',
  'Rehabilitation',
  'Telehealth',
];

const EXPERIENCE_LEVELS = [
  'New Graduate (< 1 year)',
  'Early Career (1-3 years)',
  'Mid-Level (3-7 years)',
  'Experienced (7-15 years)',
  'Senior / Expert (15+ years)',
];

// Role-category image mapping
const ROLE_IMAGES = {
  nursing: {
    banner: 'https://images.unsplash.com/photo-1765222385397-6c2ea556086f?w=800&h=400&fit=crop',
    side: 'https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=400&h=500&fit=crop',
    bottom: 'https://images.unsplash.com/photo-1758691462858-f1286e5daf40?w=800&h=350&fit=crop',
  },
  surgical: {
    banner: 'https://images.unsplash.com/photo-1639154968821-6dbc3efb8d23?w=800&h=400&fit=crop',
    side: 'https://images.unsplash.com/photo-1758691462858-f1286e5daf40?w=400&h=500&fit=crop',
    bottom: 'https://images.unsplash.com/photo-1755189118414-14c8dacdb082?w=800&h=350&fit=crop',
  },
  therapy: {
    banner: 'https://images.unsplash.com/photo-1666886573583-9839aafe43cf?w=800&h=400&fit=crop',
    side: 'https://images.unsplash.com/photo-1765222385397-6c2ea556086f?w=400&h=500&fit=crop',
    bottom: 'https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=800&h=350&fit=crop',
  },
  advanced: {
    banner: 'https://images.unsplash.com/photo-1755189118414-14c8dacdb082?w=800&h=400&fit=crop',
    side: 'https://images.unsplash.com/photo-1666886573583-9839aafe43cf?w=400&h=500&fit=crop',
    bottom: 'https://images.unsplash.com/photo-1758520144437-f068ecaf0d83?w=800&h=350&fit=crop',
  },
};

function getRoleCategory(role) {
  const r = role.toLowerCase();
  if (r.includes('or nurse') || r.includes('surgical') || r.includes('er nurse') || r.includes('icu')) return 'surgical';
  if (r.includes('therapist') || r.includes('pathologist') || r.includes('home health')) return 'therapy';
  if (r.includes('practitioner') || r.includes('physician')) return 'advanced';
  return 'nursing';
}

export const InterviewPrepPage = () => {
  usePageMeta({
    title: 'AI Interview Prep - Healthcare Interview Guide',
    description: 'Prepare for healthcare job interviews with AI-powered guides. Get tailored questions, clinical scenarios, and tips for nursing, allied health, and non-clinical roles.',
    path: '/tools/interview-prep',
  });

  const [role, setRole] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [experience, setExperience] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/interview-prep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role,
          specialty: specialty || null,
          experience_level: experience || null,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate');
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError('Unable to generate prep material right now. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const images = result ? ROLE_IMAGES[getRoleCategory(result.role)] : null;

  // Split content into sections for image interleaving
  const splitContent = (content) => {
    if (!content) return { top: '', bottom: '' };
    const lines = content.split('\n');
    const mid = Math.floor(lines.length * 0.55);
    return {
      top: lines.slice(0, mid).join('\n'),
      bottom: lines.slice(mid).join('\n'),
    };
  };

  const sections = result ? splitContent(result.content) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="interview-prep-page">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="container-width text-center relative">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6 text-sm font-medium">
            <BrainCircuit className="h-4 w-4" />
            AI-Powered Coaching
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Interview Prep Assistant
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Get role-specific interview questions, clinical scenarios, and actionable tips tailored to your healthcare career.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-width max-w-5xl">
          {/* Selection Form */}
          <Card className="p-6 md:p-8 border-border/50 mb-8" data-testid="interview-prep-form">
            <h2 className="text-lg font-bold text-foreground tracking-tight mb-1">Tell us about the role</h2>
            <p className="text-sm text-muted-foreground mb-6">Select your target position and we'll generate a tailored preparation guide.</p>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Role *</label>
                  <select value={role} onChange={e => setRole(e.target.value)} required className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="prep-role-select">
                    <option value="">Select role...</option>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Specialty</label>
                  <select value={specialty} onChange={e => setSpecialty(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="prep-specialty-select">
                    <option value="">Any specialty</option>
                    {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Experience</label>
                  <select value={experience} onChange={e => setExperience(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="prep-experience-select">
                    <option value="">Any level</option>
                    {EXPERIENCE_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <Button type="submit" disabled={loading || !role} className="bg-primary text-white rounded-full px-8 h-11 hover:translate-y-[-1px] transition-transform" data-testid="prep-generate-btn">
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Generate Prep Guide</>
                )}
              </Button>
            </form>
          </Card>

          {/* Error */}
          {error && (
            <Card className="p-4 mb-6 border-red-500/20 bg-red-500/5 text-red-600 dark:text-red-400 text-sm" data-testid="prep-error">
              {error}
            </Card>
          )}

          {/* Loading State */}
          {loading && (
            <Card className="p-16 text-center border-border/50" data-testid="prep-loading">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Loader2 className="h-7 w-7 text-primary animate-spin" />
              </div>
              <p className="text-foreground font-medium mb-1">Crafting your prep guide...</p>
              <p className="text-sm text-muted-foreground">Our AI is analyzing role requirements and building personalized material.</p>
            </Card>
          )}

          {/* Generated Result with Images */}
          {result && !loading && images && (
            <div className="space-y-6" data-testid="prep-result">
              {/* Banner Image */}
              <div className="rounded-2xl overflow-hidden shadow-lg" data-testid="prep-banner-image">
                <img
                  src={images.banner}
                  alt={`${result.role} healthcare professional`}
                  className="w-full h-48 md:h-64 object-cover"
                />
              </div>

              {/* Result Card with Side Image */}
              <Card className="border-border/50 overflow-hidden">
                <div className="px-6 md:px-8 py-5 bg-secondary/50 border-b border-border/50 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-foreground tracking-tight">{result.role}</h2>
                    <p className="text-xs text-muted-foreground">AI-Generated Interview Preparation</p>
                  </div>
                </div>

                {/* Content with side image */}
                <div className="flex flex-col lg:flex-row">
                  <div className="flex-1 p-6 md:p-8">
                    <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-li:text-muted-foreground prose-li:leading-relaxed dark:prose-invert" data-testid="prep-content">
                      <ReactMarkdown>{sections.top}</ReactMarkdown>
                    </div>
                  </div>
                  {/* Side image - desktop only */}
                  <div className="hidden lg:block w-64 shrink-0 p-4" data-testid="prep-side-image">
                    <div className="sticky top-28 space-y-4">
                      <div className="rounded-xl overflow-hidden shadow-md">
                        <img
                          src={images.side}
                          alt="Healthcare professional"
                          className="w-full h-72 object-cover"
                        />
                      </div>
                      <Card className="p-4 border-primary/20 bg-primary/5">
                        <div className="flex items-center gap-2 mb-2">
                          <Stethoscope className="h-4 w-4 text-primary" />
                          <span className="text-xs font-semibold text-primary uppercase tracking-wider">Quick Tip</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          Research the facility before your interview. Understanding their patient population and values shows genuine interest.
                        </p>
                      </Card>
                      <Card className="p-4 border-border/50">
                        <div className="flex items-center gap-2 mb-2">
                          <HeartPulse className="h-4 w-4 text-primary" />
                          <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Key Stat</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground tracking-tight">87%</p>
                        <p className="text-xs text-muted-foreground">of hiring managers say preparation is the #1 factor in candidate selection</p>
                      </Card>
                    </div>
                  </div>
                </div>

                {/* Inline image break */}
                <div className="px-6 md:px-8" data-testid="prep-inline-image">
                  <div className="rounded-xl overflow-hidden shadow-md my-2">
                    <img
                      src={images.bottom}
                      alt="Healthcare team collaboration"
                      className="w-full h-40 md:h-52 object-cover"
                    />
                  </div>
                </div>

                {/* Remaining content */}
                <div className="p-6 md:p-8 pt-4">
                  <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-li:text-muted-foreground prose-li:leading-relaxed dark:prose-invert">
                    <ReactMarkdown>{sections.bottom}</ReactMarkdown>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="px-6 md:px-8 py-6 bg-secondary/50 border-t border-border/50">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <UserCheck className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Ready to apply?</p>
                        <p className="text-xs text-muted-foreground">Put your preparation into action</p>
                      </div>
                    </div>
                    <a href="/apply-now">
                      <Button className="bg-primary text-white rounded-full px-6 hover:translate-y-[-1px] transition-transform" data-testid="prep-apply-btn">
                        Apply Now
                      </Button>
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Empty state */}
          {!result && !loading && !error && (
            <Card className="p-16 text-center border-dashed border-2 border-border/50">
              <GraduationCap className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-foreground font-medium mb-1">Ready when you are</p>
              <p className="text-sm text-muted-foreground">Select a role above to generate your personalized interview prep guide.</p>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InterviewPrepPage;
